import { useState, useEffect, useRef, useCallback } from 'react';

export const useLazyLoading = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const elementRef = useRef(null);
  
  const {
    rootMargin = '50px',
    threshold = 0.1,
    delay = 0,
    triggerOnce = true
  } = options;

  const handleIntersection = useCallback((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        
        // Add delay if specified
        if (delay > 0) {
          setTimeout(() => setIsLoaded(true), delay);
        } else {
          setIsLoaded(true);
        }
        
        // Disconnect observer if triggerOnce is true
        if (triggerOnce && elementRef.current) {
          observer.current?.unobserve(elementRef.current);
        }
      }
    });
  }, [delay, triggerOnce]);

  const observer = useRef(null);

  useEffect(() => {
    if (!elementRef.current) return;

    observer.current = new IntersectionObserver(handleIntersection, {
      rootMargin,
      threshold
    });

    observer.current.observe(elementRef.current);

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [handleIntersection, rootMargin, threshold]);

  return {
    elementRef,
    isVisible,
    isLoaded,
    shouldRender: isVisible || isLoaded
  };
};

export const useProgressiveLoading = (items, options = {}) => {
  const [visibleItems, setVisibleItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const {
    batchSize = 3,
    delay = 200,
    autoLoad = true
  } = options;

  useEffect(() => {
    if (!autoLoad || currentIndex >= items.length) return;

    const timer = setTimeout(() => {
      const nextBatch = items.slice(0, currentIndex + batchSize);
      setVisibleItems(nextBatch);
      setCurrentIndex(prev => Math.min(prev + batchSize, items.length));
    }, delay);

    return () => clearTimeout(timer);
  }, [currentIndex, items, batchSize, delay, autoLoad]);

  const loadMore = useCallback(() => {
    if (currentIndex < items.length) {
      const nextBatch = items.slice(0, currentIndex + batchSize);
      setVisibleItems(nextBatch);
      setCurrentIndex(prev => Math.min(prev + batchSize, items.length));
    }
  }, [currentIndex, items, batchSize]);

  return {
    visibleItems,
    hasMore: currentIndex < items.length,
    loadMore,
    isLoading: currentIndex < items.length && visibleItems.length < items.length
  };
};
